import React from "react";
import { Key } from "ts-key-enum";

/**
 * Closes the panel when the user picks an option, and leaves it open
 * while they are arrow-keying through the group.
 *
 * Arrow keys fire a click in Chrome and Firefox.
 * Enter fires no click on a focused radio input, so it is handled on keyup.
 *
 * #32070 removed the auto-close, #35523 restored it without breaking the keyboard.
 */

const ROVING_KEYS = new Set<string>([
    Key.ArrowUp,
    Key.ArrowDown,
    Key.ArrowLeft,
    Key.ArrowRight,
]);

const ACTIVATION_KEYS = new Set<string>([Key.Enter, " ", "Spacebar"]);

const CONFIRM_KEY: string = Key.Enter;

const OPTION_ELEMENT_SELECTOR = 'input[type="radio"], label';

type IActivationSource = "pointer" | "keyboard-activate" | "keyboard-roving";

export interface OptionActivationCloseProps {
    onPointerDownCapture: () => void;
    onKeyDownCapture: (event: React.KeyboardEvent<HTMLElement>) => void;
    onKeyUp: (event: React.KeyboardEvent<HTMLElement>) => void;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

const isOptionElement = (target: EventTarget | null): boolean => {
    const option =
        target instanceof Element
            ? target.closest(OPTION_ELEMENT_SELECTOR)
            : null;
    if (!option) {
        return false;
    }
    const control =
        option instanceof HTMLLabelElement ? option.control : option;
    return !(control instanceof HTMLInputElement && control.disabled);
};

export const useOptionActivationClose = (
    onActivate: () => void,
    enabled = true,
): OptionActivationCloseProps => {
    const sourceRef = React.useRef<IActivationSource>("pointer");
    const activatedRef = React.useRef(false);

    const activate = (): void => {
        if (activatedRef.current) {
            return;
        }
        activatedRef.current = true;
        onActivate();
    };

    return {
        onPointerDownCapture: () => {
            sourceRef.current = "pointer";
            activatedRef.current = false;
        },
        onKeyDownCapture: (event) => {
            activatedRef.current = false;
            if (ROVING_KEYS.has(event.key)) {
                sourceRef.current = "keyboard-roving";
            } else if (ACTIVATION_KEYS.has(event.key)) {
                sourceRef.current = "keyboard-activate";
            }
        },
        onKeyUp: (event) => {
            if (!enabled || event.key !== CONFIRM_KEY) {
                return;
            }
            if (!isOptionElement(event.target)) {
                return;
            }
            activate();
        },
        onClick: (event) => {
            if (!enabled || !isOptionElement(event.target)) {
                return;
            }
            if (
                event.detail > 0 ||
                sourceRef.current === "pointer" ||
                sourceRef.current === "keyboard-activate"
            ) {
                activate();
            }
        },
    };
};
